'use client';

// React Imports
import type { MouseEvent } from 'react';
import { useRef, useState } from 'react';

// Next Imports
import { useRouter } from 'next/navigation';

// MUI Imports
import Image from 'next/image';

import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

import UserInitialsAvatar from '@/components/userInitialsAva';
import { useEntityImageStore } from '@/hooks/useEntityImage';
import { useAppSelector } from '@/redux-store/hooks';
import { logout } from '@/redux-store/utils/logout';
import { EntityNameEnum } from '@/types/enums/entity-name.enum';
import { t } from '@/utils/lang';
import { useSettings } from '@core/hooks/useSettings';

const UserDropdown = () => {
    const user = useAppSelector(state => state.auth.user);

    const userInfo = useAppSelector(state => state.auth.userInformation);

    // States
    const [open, setOpen] = useState(false);

    // Refs
    const anchorRef = useRef<HTMLDivElement>(null);

    // Hooks
    const router = useRouter();

    const { settings } = useSettings();

    const { url } = useEntityImageStore(EntityNameEnum.AppUser, user?.id);

    const handleDropdownOpen = () => {
        !open ? setOpen(true) : setOpen(false);
    };

    const handleDropdownClose = (
        event?: MouseEvent<HTMLLIElement> | (MouseEvent | TouchEvent),
        url?: string
    ) => {
        if (url) {
            router.push(url);
        }

        if (
            anchorRef.current &&
            anchorRef.current.contains(event?.target as HTMLElement)
        ) {
            return;
        }

        setOpen(false);
    };

    const handleUserLogout = async () => {
        await logout();
    };

    return (
        <>
            <Badge
                ref={anchorRef}
                overlap='circular'
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                className='mis-2 cursor-pointer'
                onClick={handleDropdownOpen}
            >
                {url ? (
                    <Image
                        src={url as string}
                        alt={user?.name || 'avatar'}
                        width={38}
                        height={38}
                        className='rounded-full object-cover'
                    />
                ) : (
                    <UserInitialsAvatar
                        name={userInfo?.fullname}
                        initialsCount={2}
                        width={38}
                        height={38}
                    />
                )}
            </Badge>
            <Popper
                open={open}
                transition
                disablePortal
                placement='bottom-end'
                anchorEl={anchorRef.current}
                className='min-is-[240px] !mbs-4 z-[1]'
            >
                {({ TransitionProps, placement }) => (
                    <Fade
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom-end'
                                    ? 'right top'
                                    : 'left top',
                        }}
                    >
                        <Paper
                            className={
                                settings.skin === 'bordered'
                                    ? 'border shadow-none'
                                    : 'shadow-lg'
                            }
                        >
                            <ClickAwayListener
                                onClickAway={e =>
                                    handleDropdownClose(
                                        e as MouseEvent | TouchEvent
                                    )
                                }
                            >
                                <MenuList>
                                    <div
                                        className='flex items-center plb-2 pli-4 gap-2'
                                        tabIndex={-1}
                                    >
                                        {url ? (
                                            <Image
                                                src={url}
                                                alt={user?.name || 'avatar'}
                                                width={38}
                                                height={38}
                                                className='rounded-full object-cover'
                                            />
                                        ) : (
                                            <UserInitialsAvatar
                                                name={userInfo?.fullname}
                                                initialsCount={2}
                                            />
                                        )}
                                        <div className='flex items-start flex-col'>
                                            <Typography
                                                className='font-medium'
                                                color='text.primary'
                                            >
                                                {userInfo?.fullname}
                                            </Typography>
                                            <Typography variant='caption'>
                                                {userInfo.position || ''}
                                            </Typography>
                                        </div>
                                    </div>
                                    <Divider className='mlb-1' />
                                    <MenuItem
                                        className='gap-3'
                                        onClick={e =>
                                            handleDropdownClose(
                                                e,
                                                '/account-user'
                                            )
                                        }
                                    >
                                        <i className='ri-user-3-line' />
                                        <Typography color='text.primary'>
                                            {t('Tài khoản của tôi')}
                                        </Typography>
                                    </MenuItem>
                                    <div className='flex items-center plb-2 pli-4'>
                                        <Button
                                            fullWidth
                                            variant='contained'
                                            color='error'
                                            size='small'
                                            endIcon={
                                                <i className='ri-logout-box-r-line' />
                                            }
                                            onClick={handleUserLogout}
                                            sx={{
                                                '& .MuiButton-endIcon': {
                                                    marginInlineStart: 1.5,
                                                },
                                            }}
                                        >
                                            {t('Đăng xuất')}
                                        </Button>
                                    </div>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};

export default UserDropdown;
